import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, TextInput } from 'react-native';
import { useLocalization } from '@sendbird/uikit-react-native';
import { getGroupChannelChatAvailableState } from '@sendbird/uikit-utils';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { useI18n } from '@shared/i18n/useI18n';
import type { TUseChatComposerParams } from '../types';
import { getChannelKind } from '../utils/channelType';
import { isWelcomeMessage } from '../utils/welcomeMessage';

export const useChatComposer = ({
    channel,
    inputDisabled,
    messageToEdit,
    setMessageToEdit,
    onPressSendUserMessage,
    onPressUpdateUserMessage,
}: TUseChatComposerParams) => {
    const { t } = useI18n();
    const { STRINGS } = useLocalization();
    const inputRef = useRef<TextInput>(null);
    const [text, setText] = useState('');

    const availability = getGroupChannelChatAvailableState(channel);
    const disabled = availability.disabled ? true : inputDisabled;
    const editingMessage = messageToEdit?.isUserMessage() ? messageToEdit : undefined;
    const canSend = !disabled && text.trim().length > 0;
    const autoFocus = !channel.lastMessage || isWelcomeMessage(channel.lastMessage);

    let placeholder = STRINGS.LABELS.CHANNEL_INPUT_PLACEHOLDER_ACTIVE;
    if (availability.muted) placeholder = STRINGS.LABELS.CHANNEL_INPUT_PLACEHOLDER_MUTED;
    else if (availability.frozen || disabled) placeholder = STRINGS.LABELS.CHANNEL_INPUT_PLACEHOLDER_DISABLED;
    else if (getChannelKind(channel) === 'technicalSupport') placeholder = t('screens.chat.supportPlaceholder');

    useEffect(() => {
        const action = () => (text.length === 0 ? channel.endTyping() : channel.startTyping());
        action().catch(() => {});
    }, [text, channel]);

    useEffect(() => {
        if (disabled) setText('');
    }, [disabled]);

    useEffect(() => {
        if (!editingMessage) return;
        setText(editingMessage.message);
        const timer = setTimeout(() => inputRef.current?.focus(), 500);
        return () => clearTimeout(timer);
    }, [editingMessage]);

    const clearText = useCallback(() => {
        if (Platform.OS === 'ios') setTimeout(() => setText(''), 10);
        else setText('');
    }, []);

    const showSendError = useCallback(() => {
        ToastNotifications.displayErrorNotification(t('screens.chat.sendError'));
    }, [t]);

    const submit = useCallback(() => {
        if (!canSend) return;
        if (editingMessage) {
            onPressUpdateUserMessage(editingMessage, { message: text }).catch(showSendError);
            setMessageToEdit();
            setText('');
            return;
        }
        onPressSendUserMessage({ message: text }).catch(showSendError);
        clearText();
    }, [canSend, clearText, editingMessage, onPressSendUserMessage, onPressUpdateUserMessage, setMessageToEdit, showSendError, text]);

    const cancelEdit = useCallback(() => {
        setMessageToEdit();
        setText('');
    }, [setMessageToEdit]);

    return {
        inputRef,
        text,
        setText,
        canSend,
        disabled,
        autoFocus,
        placeholder,
        isEditing: Boolean(editingMessage),
        submit,
        cancelEdit,
    };
};
